import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Image, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Shared Event Notice content form — greeting, FAQs toggle, letterhead
 * type + image upload/preview. Used by both the standalone Event Notice
 * dialog and the Entry Pack email dialog so they share a single source
 * of truth.
 *
 * Controlled component for greeting / includeFaqs / letterheadType; letterhead
 * image upload is side-effecting (server mutation) so it's handled internally.
 */
export default function EventNoticeContentForm({
  competitionId,
  greeting,
  onGreetingChange,
  includeFaqs,
  onIncludeFaqsChange,
  letterheadType,
  onLetterheadTypeChange,
  testIdPrefix = 'en',
}) {
  const [letterheadPreview, setLetterheadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingLetterhead, setLoadingLetterhead] = useState(false);

  const fetchLetterhead = useCallback(async () => {
    if (!competitionId) return;
    setLoadingLetterhead(true);
    try {
      const checkRes = await axios.get(
        `${API}/competitions/${competitionId}`,
        { withCredentials: true }
      );
      if (!checkRes.data?.letterhead_storage_path) {
        setLetterheadPreview(null);
        return;
      }
      const res = await axios.get(
        `${API}/competitions/${competitionId}/letterhead`,
        { withCredentials: true, responseType: 'blob' }
      );
      setLetterheadPreview(URL.createObjectURL(res.data));
    } catch {
      setLetterheadPreview(null);
    } finally {
      setLoadingLetterhead(false);
    }
  }, [competitionId]);

  useEffect(() => {
    fetchLetterhead();
    return () => {
      setLetterheadPreview(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [fetchLetterhead]);

  const handleLetterheadUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid file type. Please upload a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('letterhead_type', letterheadType === 'none' ? 'top_bar' : letterheadType);
    try {
      await axios.post(
        `${API}/competitions/${competitionId}/letterhead`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Letterhead uploaded successfully');
      if (letterheadType === 'none') onLetterheadTypeChange?.('top_bar');
      fetchLetterhead();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload letterhead');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLetterhead = async () => {
    try {
      await axios.delete(
        `${API}/competitions/${competitionId}/letterhead`,
        { withCredentials: true }
      );
      setLetterheadPreview(null);
      onLetterheadTypeChange?.('none');
      toast.success('Letterhead removed');
    } catch {
      toast.error('Failed to remove letterhead');
    }
  };

  const handleLetterheadTypeChange = async (newType) => {
    onLetterheadTypeChange?.(newType);
    if (letterheadPreview && newType !== 'none') {
      try {
        await axios.patch(
          `${API}/competitions/${competitionId}/letterhead-type?letterhead_type=${newType}`,
          {},
          { withCredentials: true }
        );
      } catch {
        toast.error('Failed to update letterhead type');
      }
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleLetterheadUpload({ target: { files: [file] } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letterheadType]);

  return (
    <div className="space-y-4">
      {/* Greeting */}
      <div className="flex items-center gap-4">
        <Label className="text-sm font-semibold text-[#2D4A3E] whitespace-nowrap">
          Greeting
        </Label>
        <Input
          value={greeting}
          onChange={(e) => onGreetingChange?.(e.target.value)}
          placeholder="Enter greeting..."
          className="flex-1"
          data-testid={`${testIdPrefix}-greeting-input`}
        />
      </div>

      {/* Letterhead */}
      <div>
        <Label className="text-sm font-semibold text-[#2D4A3E] mb-2 block">
          Competition Letterhead (Optional)
        </Label>
        <div className="mb-3">
          <p className="text-sm text-stone-600 mb-2">What type of letterhead are you using?</p>
          <RadioGroup
            value={letterheadType}
            onValueChange={handleLetterheadTypeChange}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="none" id={`${testIdPrefix}-lh-none`} />
              <Label htmlFor={`${testIdPrefix}-lh-none`} className="text-sm font-normal cursor-pointer">No image</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="top_bar" id={`${testIdPrefix}-lh-top`} />
              <Label htmlFor={`${testIdPrefix}-lh-top`} className="text-sm font-normal cursor-pointer">
                Top bar (210mm wide × maximum 30mm high)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="side_bar" id={`${testIdPrefix}-lh-side`} />
              <Label htmlFor={`${testIdPrefix}-lh-side`} className="text-sm font-normal cursor-pointer">
                Side bar (297mm high × maximum 40mm wide)
              </Label>
            </div>
          </RadioGroup>
        </div>

        {loadingLetterhead ? (
          <div className="border rounded-lg p-6 text-center bg-stone-50">
            <Loader2 className="h-8 w-8 animate-spin text-green-600 mx-auto" />
            <span className="text-sm text-stone-500 mt-2 block">Checking for letterhead...</span>
          </div>
        ) : letterheadPreview ? (
          <div className="relative border rounded-lg p-4 bg-white">
            <img
              src={letterheadPreview}
              alt="Letterhead preview"
              className={letterheadType === 'side_bar' ? 'max-h-48 max-w-20 object-contain' : 'max-h-32 mx-auto object-contain'}
              data-testid={`${testIdPrefix}-letterhead-preview`}
            />
            <button
              onClick={handleRemoveLetterhead}
              className="absolute top-2 right-2 p-1 bg-red-100 hover:bg-red-200 rounded-full text-red-600"
              data-testid={`${testIdPrefix}-remove-letterhead-btn`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer"
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={handleDrop}
            onClick={() => document.getElementById(`${testIdPrefix}-letterhead-upload`).click()}
            data-testid={`${testIdPrefix}-letterhead-dropzone`}
          >
            <input
              id={`${testIdPrefix}-letterhead-upload`}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleLetterheadUpload}
              className="hidden"
              data-testid={`${testIdPrefix}-letterhead-input`}
            />
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                <span className="text-sm text-stone-500 mt-2">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="p-3 bg-stone-100 rounded-full mb-3">
                  <Image className="h-6 w-6 text-stone-400" />
                </div>
                <p className="text-sm font-medium text-stone-700">
                  Drag and drop your letterhead here
                </p>
                <p className="text-xs text-stone-500 mt-1">
                  or click to browse (PNG, JPEG, WebP up to 5MB)
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* FAQs */}
      <div className="rounded-lg border bg-stone-50 px-3 py-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${testIdPrefix}-include-faqs`}
            checked={!!includeFaqs}
            onCheckedChange={(v) => onIncludeFaqsChange?.(!!v)}
            data-testid={`${testIdPrefix}-include-faqs-checkbox`}
          />
          <Label htmlFor={`${testIdPrefix}-include-faqs`} className="text-sm font-normal cursor-pointer">
            Include the Frequently Asked Questions section (music / content sheet / entries / banking info).
          </Label>
        </div>
      </div>
    </div>
  );
}
